import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, Calendar, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const readingTime = (text = '') => {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

const renderContent = (content = '') => {
  // Simple renderer: split by blank lines to make paragraphs, preserve line breaks
  const blocks = content.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  return blocks.map((block, i) => (
    <p key={i} className="mb-5 text-base font-body text-[#333] leading-[1.85] whitespace-pre-line">
      {block}
    </p>
  ));
};

const ArticleReader = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | found | notfound

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/articles`);
        if (!mounted) return;
        const all = res.data;
        const match = all.find((a) => a.slug === slug);
        if (!match) {
          setStatus('notfound');
          return;
        }
        setArticle(match);
        setStatus('found');
        setRelated(
          all
            .filter((a) => a.article_id !== match.article_id && a.category === match.category)
            .slice(0, 3)
        );
        window.scrollTo(0, 0);
      } catch (e) {
        if (mounted) setStatus('notfound');
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <p className="text-sm font-body text-[#737373]">Loading article...</p>
      </div>
    );
  }

  if (status === 'notfound') {
    return (
      <div className="min-h-screen pt-32 pb-20 px-4" data-testid="article-not-found">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl font-heading font-medium text-[#A0451E] mb-4">Article not found</h1>
          <p className="text-base font-body text-[#4A4A4A] mb-8">
            The article you're looking for is not available or may have been unpublished.
          </p>
          <button onClick={() => navigate('/education')} className="btn-primary" data-testid="article-back-to-list-btn">
            Back to Education Centre
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="article-reader">
      {/* Hero */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-[#FDF8F5]">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/education"
            className="inline-flex items-center gap-2 text-sm font-body text-[#A0451E] hover:text-[#D97706] mb-6 transition"
            data-testid="article-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Investor Education Centre
          </Link>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 bg-[#D97706] text-white text-xs px-3 py-1 rounded-full font-body">
              <Tag className="w-3 h-3" />
              {article.category}
            </span>
            <span className="inline-block bg-white text-[#A0451E] text-xs px-3 py-1 rounded-full font-body border border-[#E5E5E5] capitalize">
              {article.difficulty_level}
            </span>
          </div>

          <h1
            className="text-4xl sm:text-5xl font-heading font-medium text-[#A0451E] mb-6 leading-tight"
            data-testid="article-title"
          >
            {article.title}
          </h1>

          <p className="text-lg font-body text-[#4A4A4A] leading-relaxed mb-6" data-testid="article-excerpt">
            {article.excerpt}
          </p>

          <div className="flex flex-wrap items-center gap-4 text-sm font-body text-[#737373] pt-4 border-t border-[#E5E5E5]">
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {readingTime(article.content)} min read
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatDate(article.created_at)}
            </span>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <article data-testid="article-content" className="prose-impetus">
            {renderContent(article.content)}
          </article>

          {/* Disclaimer */}
          <div className="mt-12 p-6 bg-[#FDF8F5] border border-[#E5E5E5] rounded-xl">
            <p className="text-xs font-body text-[#737373] leading-relaxed">
              <strong className="text-[#A0451E]">Disclaimer:</strong> This article is for general educational purposes
              only and does not constitute personalised investment, legal or tax advice. Mutual Fund investments are
              subject to market risks. Read all scheme-related documents carefully.
            </p>
          </div>
        </div>
      </section>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="py-16 section-bg-secondary" data-testid="related-articles-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-heading font-medium text-[#A0451E] mb-8">More in {article.category}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((a, index) => (
                <button
                  key={a.article_id}
                  onClick={() => navigate(`/education/${a.slug}`)}
                  className="text-left bg-white rounded-xl p-5 border border-[#E5E5E5] card-hover"
                  data-testid={`related-article-${index}`}
                >
                  <span className="inline-block bg-[#FDF8F5] text-[#A0451E] text-xs px-2 py-0.5 rounded-full font-body border border-[#E5E5E5] capitalize mb-2">
                    {a.difficulty_level}
                  </span>
                  <h3 className="text-base font-heading font-medium text-[#A0451E] mb-2 leading-snug">{a.title}</h3>
                  <p className="text-xs font-body text-[#737373] line-clamp-2">{a.excerpt}</p>
                  <p className="mt-3 text-xs font-body font-semibold text-[#D97706]">
                    {readingTime(a.content)} min read &rarr;
                  </p>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E] mb-4">
            Want to apply this to your portfolio?
          </h2>
          <p className="text-base font-body text-[#4A4A4A] mb-6">
            Schedule a conversation with our team to see how these concepts fit your specific goals.
          </p>
          <button
            onClick={() => navigate('/appointment')}
            className="btn-primary"
            data-testid="article-book-appointment-btn"
          >
            Book an Appointment
          </button>
        </div>
      </section>
    </div>
  );
};

export default ArticleReader;
